import UserClient from '@/api/UserClient';
import CampusClient from '@/api/CampusClient';
import PlatformClient from '@/api/PlatformClient';

export default class EditSchoolModel {
  saving = false;

  features = [];

  logo = {
    url: ''
  };

  banner = {
    url: ''
  };

  general = {
    name: '',
    campusCrisisInfo: '',
    messageForNewUserPrompt: '',
    counselingCenterLocation: '',
    counselingCenterPhone: '',
    isMember: false
  };

  primaryContact = {
    name: '',
    title: '',
    phoneNumber: '',
    email: '',
    contactInCrisis: false
  };

  crisisContact = {
    name: '',
    title: '',
    phoneNumber: '',
    email: '',
    contactInCrisis: false
  };

  resources = {
    local: '',
    campus: ''
  };

  counselingCenter = {
    name: '',
    location: '',
    phoneNumber: ''
  };

  triage = {
    crisis: '',
    nonCrisis: '',
    creditPolicy: ''
  };

  integrations = {
    sftp: {
      folder: '',
      publicKey: ''
    }
  };

  credits = {
    remainingSessionCredits: '',
    remainingWorkshopCredits: ''
  };

  constructor(http) {
    this.userClient = new UserClient(http);
    this.campusClient = new CampusClient(http);
    this.platformClient = new PlatformClient(http);
  }

  initialize(schoolId) {
    this.id = schoolId;

    return this.reset();
  }

  async updateLogo(file) {
    if (!file || !this.id) return;

    try {
      this.saving = true;

      const logoPhotoMeta = await this.userClient.getSignedUploadUrl();
      await UserClient.saveFile(logoPhotoMeta.signed_url, file);

      const request = { logo: { fileId: logoPhotoMeta.id } };

      await this.updateAndReset(request);
    } finally {
      this.saving = false;
    }
  }

  async updateBanner(file) {
    if (!file || !this.id) return;

    try {
      this.saving = true;

      const bannerPhotoMeta = await this.userClient.getSignedUploadUrl();
      await UserClient.saveFile(bannerPhotoMeta.signed_url, file);

      await this.updateAndReset({ banner: { fileId: bannerPhotoMeta.id } });
    } finally {
      this.saving = false;
    }
  }

  async updateGeneral(general) {
    const request = {};

    for (const key in this.general) {
      if (general[key] != this.general[key]) request[key] = general[key];
    }

    await this.updateAndReset({ general: request });
  }

  async updatePrimaryContact(primaryContact) {
    const request = {};

    for (const key in this.primaryContact) {
      if (primaryContact[key] != this.primaryContact[key]) {
        request[key] = primaryContact[key];
      }
    }

    await this.updateAndReset({ primaryContact: request });
  }

  async updateCrisisContact(crisisContact) {
    const request = {};

    for (const key in this.crisisContact) {
      if (crisisContact[key] != this.crisisContact[key]) {
        request[key] = crisisContact[key];
      }
    }

    await this.updateAndReset({ crisisContact: request });
  }

  async updateResources(resources) {
    const request = {};

    for (const key in this.resources) {
      if (resources[key] != this.resources[key]) request[key] = resources[key];
    }

    await this.updateAndReset({ resources: request });
  }

  async updateCounselingCenter(counselingCenter) {
    const request = {};

    for (const key in this.counselingCenter) {
      if (counselingCenter[key] != this.counselingCenter[key]) {
        request[key] = counselingCenter[key];
      }
    }

    await this.updateAndReset({ counselingCenter: request });
  }

  async updateTriage(triage) {
    const request = {};

    for (const key in this.triage) {
      if (triage[key] != this.triage[key]) request[key] = triage[key];
    }

    await this.updateAndReset({ triage: request });
  }

  async updateIntegrations(integrations) {
    const request = { sftp: {} };

    for (const key in this.integrations.sftp) {
      if (integrations.sftp[key] != this.integrations.sftp[key]) {
        request.sftp[key] = integrations.sftp[key];
      }
    }

    await this.updateAndReset({ integrations: request });
  }

  async setFeature(features) {
    try {
      this.saving = true;

      for (const key in features) {
        if (features[key]) {
          await this.platformClient.setFeature({ schoolId: this.id }, key);
        } else {
          await this.platformClient.deleteFeature({ schoolId: this.id }, key);
        }
      }

      return features;
    } finally {
      this.saving = false;
    }
  }

  async updateAndReset(update) {
    try {
      this.saving = true;

      await this.campusClient.setProfile(this.id, update);

      await this.reset();
    } finally {
      this.saving = false;
    }
  }

  async updateSessionCreditsAndReset(option, credits) {
    try {
      this.saving = true;
      if (option === 'add') {
        await this.campusClient.addSessionCredits(this.id, credits);
      } else await this.campusClient.removeSessionCredits(this.id, credits);

      await this.reset();
    } finally {
      this.saving = false;
    }
  }

  async updateWorkshopCreditsAndReset(option, credits) {
    try {
      this.saving = true;
      if (option === 'add') {
        await this.campusClient.addWorkshopCredits(this.id, credits);
      } else await this.campusClient.removeWorkshopCredits(this.id, credits);

      await this.reset();
    } finally {
      this.saving = false;
    }
  }

  async reset() {
    const features = this.platformClient.getFeatures({ schoolId: this.id });
    const school = await this.campusClient.getProfile(this.id);

    this.features.length = 0;
    this.features.push(...(await features));

    this.logo.url = school.logo.url || '';
    this.banner.url = school.banner.url || '';

    this.general.name = school.general.name || '';
    this.general.campusCrisisInfo = school.general.campusCrisisInfo || '';
    this.general.messageForNewUserPrompt = school.general.messageForNewUserPrompt || '';
    this.general.counselingCenterLocation = school.general.counselingCenterLocation || '';
    this.general.counselingCenterPhone = school.general.counselingCenterPhone || '';
    this.general.isMember = school.general.isMember || false;

    this.primaryContact.name = school.primaryContact.name || '';
    this.primaryContact.title = school.primaryContact.title || '';
    this.primaryContact.phoneNumber = school.primaryContact.phoneNumber || '';
    this.primaryContact.email = school.primaryContact.email || '';
    this.primaryContact.contactInCrisis = school.primaryContact.contactInCrisis || false;

    this.crisisContact.name = school.crisisContact.name || '';
    this.crisisContact.title = school.crisisContact.title || '';
    this.crisisContact.phoneNumber = school.crisisContact.phoneNumber || '';
    this.crisisContact.email = school.crisisContact.email || '';
    this.crisisContact.contactInCrisis = school.crisisContact.contactInCrisis;

    this.resources.local = school.resources.local || '';
    this.resources.campus = school.resources.campus || '';

    this.counselingCenter.name = school.counselingCenter.name || '';
    this.counselingCenter.location = school.counselingCenter.location || '';
    this.counselingCenter.phoneNumber = school.counselingCenter.phoneNumber || '';

    this.triage.crisis = school.triage.crisis || '';
    this.triage.nonCrisis = school.triage.nonCrisis || '';
    this.triage.creditPolicy = school.triage.creditPolicy || '';

    this.integrations.sftp.folder = school.integrations.sftp.folder || '';
    this.integrations.sftp.publicKey = school.integrations.sftp.publicKey || '';

    this.credits.remainingSessionCredits = school.credits.remainingSessionCredits || '';
    this.credits.remainingWorkshopCredits = school.credits.remainingWorkshopCredits || '';
  }
}
